import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from 'react';

const Login = () => {
    const API_URL = import.meta.env.VITE_API_URL;
    const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [form, setForm] = useState({id: "", password: ""});
	const [formErrors, setFormErrors] = useState({id: "", password: ""});
	const [loginError, setLoginError] = useState("");

	const mutation = useMutation({
		mutationFn: async (data: { id: string; password: string }) => {
			const params = new URLSearchParams();
			Object.entries(data).forEach(([key, value]) => {
				params.append(key, value);
			});

			const response = await fetch(`${API_URL}/bbs/ajax.login.php`, {
				method: "POST",
				headers: {
					"Content-Type": "application/x-www-form-urlencoded",
				},
				credentials: "include",
				body: params,
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		onSuccess: (response) => {
			if(!response.success) {
				switch (response.code) {
				case "INVALID_REQUEST":
					setLoginError("아이디, 비밀번호가 입력되지 않았습니다.");
					break;
				case "NOT_MATCH_MEMBER_PASSWORD":
					setLoginError("아이디 또는 비밀번호가 일치하지 않습니다.");
					break;
				case "INTERCEPT_MEMBER":
					setLoginError("차단된 회원입니다.");
					break;
				case "LEAVE_MEMBER":
					setLoginError("탈퇴한 회원입니다.");
					break;
				default:
					setLoginError("알 수 없는 오류가 발생했습니다.");
				}
				return;
			}

			if(!response.data) {
				setLoginError("알 수 없는 오류가 발생했습니다.");
				return;
			}

			if(response.data.memberId) {
				queryClient.invalidateQueries({ queryKey: ['auth'] });
				navigate("/", {replace: true});
			}
		},
		onError: (error: any) => {
			setLoginError(error.message);
		}
	});

	const validate = (name: string, value: string) => {
		let validValue = value;
		let errorMsg = "";

		if (name === "id") {
			if (!value.trim()) {
				errorMsg = "아이디를 입력해주세요.";
			} else if (/[^a-zA-Z0-9]/.test(value)) {
				errorMsg = "아이디는 영문, 숫자만 입력 가능합니다.";
			} else if (value.length < 3) {
				errorMsg = "아이디는 3자 이상 입력해주세요.";
			}
		}

		if (name === "password") {
			if (!value.trim()) {
				errorMsg = "비밀번호를 입력해주세요.";
			} else if (value.length < 4) {
				errorMsg = "비밀번호는 4자 이상 입력해주세요.";
			}
			validValue = value.replace(/\s/g, "");
		}

		return {
			value: validValue,
			error: errorMsg,
		};
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		const result = validate(name, value);
		setLoginError("");
		setForm(prev => ({ ...prev, [name]: result.value }));
		setFormErrors(prev => ({ ...prev, [name]: result.error }));
	};

	const handleLogin = () => {
		const { value: id, error: idError } = validate("id", form.id);
		const { value: password, error: pwError } = validate("password", form.password);
		if (idError || pwError) {
			setFormErrors({id: idError, password: pwError});
			return;
		}
		mutation.mutate({ id, password });
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === "Enter") handleLogin();
	};

	return (
		<section id="login" className="min-h-[70vh] flex items-center py-12 md:py-16 bg-gradient-to-br from-purple-600 to-blue-600">
			<div className="max-w-7xl mx-auto px-4 w-full">
				<div className="max-w-md md:max-w-lg mx-auto">
					<div className="text-center mb-10">
						<h2 className="text-3xl md:text-4xl font-bold text-white tracking-tight">
							로그인
						</h2>
						<p className="text-white/80 mt-2 text-sm">
							서비스를 이용하려면 로그인해주세요
						</p>
					</div>
					<div className="bg-white/95 backdrop-blur rounded-2xl p-6 md:p-8 shadow-xl border border-white/20">
						<div className="space-y-5">
							<div>
								<label htmlFor="id" className="block text-sm font-semibold text-gray-700 mb-1">
									아이디
								</label>
								<input
									type="text"
									id="id"
									name="id"
									value={form.id}
									onChange={handleChange}
									disabled={mutation.isPending}
									placeholder="아이디를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.id ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.id && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.id}
									</p>
								)}
							</div>
							<div>
								<label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-1">
									비밀번호
								</label>
								<input
									type="password"
									id="password"
									name="password"
									value={form.password}
									onChange={handleChange}
									disabled={mutation.isPending}
									placeholder="비밀번호를 입력해주세요"
									className={`w-full px-4 py-3 rounded-lg border text-sm transition
      									${formErrors.password ? "border-red-500 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"}
							      	`}
								/>
								{formErrors.password && (
									<p className="text-red-500 text-xs mt-1 min-h-[16px]">
										{formErrors.password}
									</p>
								)}
							</div>
						</div>
						<button
							onClick={handleLogin}
							className="w-full py-3 rounded-lg font-semibold transition bg-gradient-to-r from-purple-600 to-blue-600 text-white hover:opacity-90 mt-3"
						>
							로그인
						</button>
						{loginError && (
							<p className="text-red-500 text-sm mt-3 text-center">
								{loginError}
							</p>
						)}
						<button
							onClick={() => navigate('/member/join')}
							className="w-full py-3 mt-3 rounded-lg font-semibold bg-gradient-to-r from-purple-200 to-blue-200 shadow-sm text-gray-700"
						>
							회원가입
						</button>
					</div>
				</div>
			</div>
		</section>
	);
}

export default Login;